import React, { useEffect, useState } from "react";
import { collection, doc, getDocs, getDoc, updateDoc, query, where } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../firebase";
import { acceptRequest, getMyAnonAdIds, isPending, perAd } from "../../penpal";
import { useBlocks } from "../../blocks";
import { Link } from "react-router-dom";
import Window from "../../components/Window";
import Icon, { IconTile } from "../../components/Icon";
import { Avatar } from "../../components/ProfileBits";
import { SafetyActions } from "../../components/SafetyBits";
import "../../styles.css";

interface PenPalRequest {
  id: string;
  from: string;
  to?: string;
  toAd?: string; // sent to an anonymous ad
  message: string;
  status: string;
  senderName?: string;
  senderUsername?: string;
  senderPronouns?: string;
  senderAvatar?: { config?: unknown; icon?: string; tone?: string };
}

export default function PenPalRequestsPage() {
  const [user] = useAuthState(auth);
  const { ids: blocked } = useBlocks();
  const [requests, setRequests] = useState<PenPalRequest[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const fetchRequests = async () => {
      // Requests to this user, plus ones sent to their anonymous ads.
      const anonIds = await getMyAnonAdIds(user.uid).catch(() => [] as string[]);
      const snaps = await Promise.all([
        getDocs(query(collection(db, "penpalRequests"), where("to", "==", user.uid))),
        ...perAd(anonIds, (adId) =>
          getDocs(query(collection(db, "penpalRequests"), where("toAd", "==", adId)))
        ),
      ]);
      const byId = new Map<string, PenPalRequest>();
      snaps
        .flatMap((s) => s.docs)
        .forEach((d) => byId.set(d.id, { ...(d.data() as PenPalRequest), id: d.id }));
      const pending = Array.from(byId.values()).filter((r) => isPending(r.status));

      const withSenderInfo = await Promise.all(
        pending.map(async (req) => {
          const senderSnap = await getDoc(doc(db, "users", req.from));
          const sender = senderSnap.exists() ? senderSnap.data() : {};
          return {
            ...req,
            senderUsername: sender.username || "",
            senderName:
              (sender.visibility?.name && sender.name) ||
              (sender.username ? `@${sender.username}` : "Someone"),
            senderPronouns: sender.visibility?.pronouns ? sender.pronouns : "",
            senderAvatar: {
              config: sender.avatarConfig,
              icon: sender.avatar,
              tone: sender.avatarTone,
            },
          };
        })
      );
      setRequests(withSenderInfo);
      setLoaded(true);
    };
    fetchRequests().catch((err) => {
      console.error(err);
      setLoaded(true);
    });
  }, [user]);

  const visible = requests.filter((r) => !blocked.has(r.from));

  const handleAccept = async (req: PenPalRequest) => {
    if (!user) return;
    if (req.toAd) {
      const ok = window.confirm(
        "This request came through your anonymous ad. Accepting will show " +
          `${req.senderName} who you are. Accept?`
      );
      if (!ok) return;
    }
    setBusy(req.id);
    try {
      await acceptRequest(req.id, req.from, user.uid, !!req.toAd);
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (err) {
      console.error(err);
      alert("Sorry, that didn't work. Please try again.");
    }
    setBusy(null);
  };

  const answer = async (req: PenPalRequest, status: "maybe" | "declined") => {
    setBusy(req.id);
    try {
      await updateDoc(doc(db, "penpalRequests", req.id), { status });
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (err) {
      console.error(err);
    }
    setBusy(null);
  };

  return (
    <>
      <Window title="requests.exe">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="window-heading flex flex-1 items-center gap-3">
            <IconTile name="request" tone="gold" size={44} /> Pen Pal Requests
          </h1>
          <Link to="/address-book" className="btn btn-plain btn-sm">
            <Icon name="book" size={14} /> My pen pals
          </Link>
        </div>
        <p className="window-sub !mb-0">
          Folks who'd like to write to you. Accept to become pen pals and start
          writing letters. "Maybe later" and "No thanks" are always okay, and
          they aren't told which you chose.
        </p>
      </Window>

      <Window title={`incoming (${visible.length})`} tone="tan">
        {!loaded ? (
          <p className="empty">Checking the mail slot...</p>
        ) : visible.length === 0 ? (
          <p className="empty">
            <span className="empty-icon">
              <Icon name="stars" size={32} tone="violet" pop />
            </span>
            No new requests right now. Check back soon!
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {visible.map((req) => (
              <div key={req.id} className="profile-box bg-white">
                <div className="profile-box-head flex items-center gap-2">
                  <span className="new-badge">NEW!</span> request
                  {req.toAd && " · via your anonymous ad"}
                </div>
                <div className="profile-box-body flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <Avatar
                      config={req.senderAvatar?.config}
                      icon={req.senderAvatar?.icon}
                      tone={req.senderAvatar?.tone}
                      size={48}
                    />
                    <p className="min-w-0 font-display text-lg font-semibold">
                      {req.senderName}
                      {req.senderPronouns && (
                        <span className="block text-sm font-normal text-ink-soft">
                          ({req.senderPronouns})
                        </span>
                      )}
                    </p>
                  </div>
                  {req.message && (
                    <p className="whitespace-pre-wrap font-hand text-lg">{req.message}</p>
                  )}
                  <div className="btn-row">
                    <button
                      onClick={() => handleAccept(req)}
                      className="btn btn-sm"
                      disabled={busy === req.id}
                    >
                      <Icon name="check" size={16} /> Accept
                    </button>
                    <button
                      onClick={() => answer(req, "maybe")}
                      className="btn btn-plain btn-sm"
                      disabled={busy === req.id}
                    >
                      Maybe later
                    </button>
                    <button
                      onClick={() => answer(req, "declined")}
                      className="btn btn-plain btn-sm"
                      disabled={busy === req.id}
                    >
                      No thanks
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <Link to={`/profile/${req.from}`}>View profile</Link>
                    <SafetyActions
                      target={{
                        userId: req.from,
                        username: req.senderUsername,
                        requestId: req.id,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Window>
    </>
  );
}
